import React, { useEffect, useState } from 'react';
import { notificationsAPI } from '../../services/api';
import { Notification as AppNotification } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { Bell, Circle, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatServerDate } from '../../utils/datetime';
import { getIconForType, getLinkForType } from '../notifications/notificationsHelpers';

interface NotificationsTabProps {
    onEmailNotificationClick: (threadId: string) => void;
}

const NotificationsTab: React.FC<NotificationsTabProps> = ({ onEmailNotificationClick }) => {
    const { showToast } = useToast();
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [unreadOnly, setUnreadOnly] = useState(false);

    useEffect(() => {
        fetchNotifications();
    }, [unreadOnly]);

    const fetchNotifications = async () => {
        setLoadingNotifications(true);
        try {
            const res = await notificationsAPI.list(unreadOnly ? true : undefined);
            setNotifications(res.data);
        } catch (err: any) {
            showToast('Failed to load notifications', 'error');
        } finally {
            setLoadingNotifications(false);
        }
    };

    const handleMarkAsRead = async (id: string) => {
        try {
            await notificationsAPI.markAsRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        } catch (err) {
            showToast('Failed to mark as read', 'error');
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationsAPI.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            showToast('All notifications marked as read', 'success');
        } catch (err) {
            showToast('Failed to mark all as read', 'error');
        }
    };

    return (
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="w-full max-w-4xl mx-auto space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                            Notifications
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">Stay updated with your latest alerts.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={() => setUnreadOnly(!unreadOnly)}
                            className={`text-sm font-medium px-3 py-1.5 rounded-xl border transition-all ${unreadOnly
                                ? 'bg-primary/10 border-primary text-primary'
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                        >
                            Unread Only
                        </button>
                        <button
                            onClick={handleMarkAllAsRead}
                            className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm text-sm font-medium"
                        >
                            <Check className="w-4 h-4" />
                            <span>Mark all read</span>
                        </button>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
                    {loadingNotifications ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="p-12 text-center">
                            <Bell className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400">You're all caught up!</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`p-4 md:p-5 flex items-start gap-4 transition-colors ${notification.is_read ? 'bg-transparent' : 'bg-primary/5 dark:bg-primary/10'
                                        }`}
                                >
                                    <div className="flex-shrink-0 mt-1">
                                        <div className={`p-2 rounded-xl ${notification.is_read ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-gray-700 shadow-sm'}`}>
                                            {getIconForType(notification.type)}
                                        </div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <Link
                                                    to={notification.metadata?.link || getLinkForType(notification.type)}
                                                    className="font-semibold text-gray-900 dark:text-white hover:text-primary transition-colors"
                                                    onClick={(e) => {
                                                        if (!notification.is_read) handleMarkAsRead(notification.id);
                                                        if (notification.type.startsWith('email_')) {
                                                            e.preventDefault();
                                                            if (notification.metadata?.thread_id) {
                                                                onEmailNotificationClick(notification.metadata.thread_id);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {notification.title}
                                                </Link>
                                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
                                                    {notification.message}
                                                </p>
                                            </div>
                                            <div className="flex-shrink-0 flex flex-col items-end gap-2">
                                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                                    {formatServerDate(notification.created_at)}
                                                </span>
                                                {!notification.is_read && (
                                                    <button
                                                        onClick={() => handleMarkAsRead(notification.id)}
                                                        className="p-1 text-primary hover:bg-primary/10 rounded-full transition-colors"
                                                        title="Mark as read"
                                                    >
                                                        <Circle className="w-3 h-3 fill-current" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationsTab;
